// Small HTTP resilience helpers — tolerate Azure Container Apps "scale-to-zero"
// cold starts (and the serverless DB resuming) without surfacing scary errors.
//
// On the first request after the app has been idle, the Container Apps edge may
// return a 502/503/504 (or the request times out) *before* the app — and its CORS
// headers — are ready. Those are transient: a short backoff + retry succeeds once
// the replica is warm. Real failures (401/403/404/422/...) are NOT retried.
import axios from 'axios';

const TRANSIENT_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

export function isTransient(err) {
  if (axios.isCancel?.(err)) return false;
  const status = err?.response?.status;
  // No response at all = network error / timeout / cold-start drop → transient.
  if (status == null) return true;
  return TRANSIENT_STATUS.has(status);
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const backoff = (attempt, base) => Math.min(base * 2 ** (attempt - 1), 8000);

/**
 * Run an async thunk with retry + exponential backoff on transient errors.
 * @param {() => Promise<any>} thunk
 * @param {{retries?:number, baseDelay?:number, onRetry?:(attempt:number)=>void}} opts
 */
export async function withRetry(thunk, { retries = 4, baseDelay = 800, onRetry } = {}) {
  let attempt = 0;
  for (;;) {
    try {
      return await thunk();
    } catch (err) {
      if (attempt >= retries || !isTransient(err)) throw err;
      attempt += 1;
      try { onRetry?.(attempt); } catch { /* ignore */ }
      await sleep(backoff(attempt, baseDelay));
    }
  }
}

/**
 * Attach a response-error interceptor that retries transient failures in-place.
 * Used for the authenticated `api` instance so a mid-session cold start is
 * absorbed transparently.
 */
export function installRetryInterceptor(instance, { retries = 3, baseDelay = 800, onRetry } = {}) {
  instance.interceptors.response.use(undefined, async (err) => {
    const cfg = err?.config;
    if (!cfg || !isTransient(err)) return Promise.reject(err);
    cfg.__retryCount = (cfg.__retryCount || 0) + 1;
    if (cfg.__retryCount > retries) return Promise.reject(err);
    try { onRetry?.(cfg.__retryCount); } catch { /* ignore */ }
    await sleep(backoff(cfg.__retryCount, baseDelay));
    return instance(cfg);
  });
  return instance;
}
